import {
  KERNING_PRESET_VALUES,
  SPACING_PRESET_LABELS,
  TRACKING_PRESET_VALUES,
} from '../constants/typography-ranges'

/** Returns true when `value` is exactly one of the three kerning presets. */
export function isKerningPresetValue(value: number): boolean {
  return KERNING_PRESET_VALUES.some((v) => v === value)
}

/** Returns true when `value` is exactly one of the three tracking presets. */
export function isTrackingPresetValue(value: number): boolean {
  return TRACKING_PRESET_VALUES.some((v) => v === value)
}

/**
 * Returns the preset value closest to `value`. Ties prefer the lower preset.
 */
export function nearestPreset(value: number, presets: readonly number[]): number | null {
  if (presets.length === 0) return null

  let closest = presets[0]
  let minDist = Math.abs(value - closest)

  for (let i = 1; i < presets.length; i++) {
    const preset = presets[i]
    const dist = Math.abs(value - preset)

    if (dist < minDist || (dist === minDist && preset < closest)) {
      minDist = dist
      closest = preset
    }
  }

  return closest
}

/** The chip label for a preset value, by its index in `presets`. */
function labelForPreset(preset: number, presets: readonly number[]): string {
  return SPACING_PRESET_LABELS[presets.indexOf(preset)] ?? ''
}

export function nearestKerningLabel(value: number): string {
  const nearest = nearestPreset(value, KERNING_PRESET_VALUES)

  return nearest != null ? labelForPreset(nearest, KERNING_PRESET_VALUES) : ''
}

export function nearestTrackingLabel(value: number): string {
  const nearest = nearestPreset(value, TRACKING_PRESET_VALUES)

  return nearest != null ? labelForPreset(nearest, TRACKING_PRESET_VALUES) : ''
}

/** The preset value a chip label stands for, or `undefined` for anything that isn't a chip label
 *  (e.g. the empty string a single-select toggle group emits when its active chip is re-clicked). */
export function presetValueForLabel(label: string, presets: readonly number[]): number | undefined {
  const index = (SPACING_PRESET_LABELS as readonly string[]).indexOf(label)

  return index >= 0 ? presets[index] : undefined
}

/** Which Kerning/Tracking chip should read as active for the field's current text: only an exact
 *  preset value lights one up, and a blank or mixed field lights none. */
export function resolvePresetChipLabel(
  inputValue: string,
  mixed: boolean,
  isPresetValue: (value: number) => boolean,
  nearestLabel: (value: number) => string
): string {
  if (mixed || inputValue === '') return ''
  const parsed = parseFloat(inputValue)

  return isFinite(parsed) && isPresetValue(parsed) ? nearestLabel(parsed) : ''
}
